/* Phase 2.2B — Government & legislative resources editor.
   Edits office details, officeholder metadata, contacts, constituent-service
   links, verification, and discovery visibility for government records.
   Legislative offices remain Discovery Only (enforced server-side). */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Search, X, Edit3, ShieldCheck, ShieldAlert, RotateCcw, ExternalLink } from "lucide-react";
import { AdminGate, AdminShell, Field, styles } from "@/pages/AdminWorkspace";
import { useUserAuth } from "@/context/UserAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTY_LABEL = { D: "Democrat", R: "Republican", I: "Independent" };

function VerificationBadge({ value }) {
  const map = {
    verified: ["bg-green-50 text-green-800 border border-green-200", "Verified"],
    stale: ["bg-amber-50 text-amber-800 border border-amber-200", "Needs re-verification"],
    unverified: ["bg-navy/5 text-navy/60 border border-navy/15", "Unverified"],
  };
  const [cls, label] = map[value] || map.unverified;
  return <span className={`${styles.chip} ${cls}`} data-testid={`verification-${value || "unverified"}`}>{label}</span>;
}

export default function AdminGovernment() {
  const { authHeaders } = useUserAuth();
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/government-resources`, { headers: authHeaders() });
    setList(r.data.resources || []);
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(""), 3000); };

  const filtered = (list || []).filter((r) =>
    !q || [r.display_name, r.officeholder_name, r.district_code].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  const save = async (payload) => {
    await axios.patch(`${API}/admin/government-resources/${editing.id}`, payload, { headers: authHeaders() });
    setEditing(null);
    flash("Government resource updated.");
    load();
  };

  const verify = async (source, note) => {
    await axios.post(`${API}/admin/government-resources/${verifying.id}/verify`, { source, note: note || undefined }, { headers: authHeaders() });
    setVerifying(null);
    flash("Marked as verified.");
    load();
  };

  const markStale = async (r) => {
    if (!window.confirm(`Mark ${r.display_name} as needing re-verification?`)) return;
    await axios.post(`${API}/admin/government-resources/${r.id}/verification/reset`, {}, { headers: authHeaders() });
    flash("Verification marked stale.");
    load();
  };

  return (
    <AdminGate>
      <AdminShell title="Government & Legislative Resources" testid="admin-government-page">
        <p className="mb-5 text-sm text-slateSoft max-w-3xl">
          Enduring offices and their current officeholders. Party affiliation is factual metadata only and never
          influences resource ranking or access. <strong>Legislative offices remain Discovery Only</strong> during the pilot —
          no referrals are routed to them.
        </p>
        {status && <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="gov-flash">{status}</p>}

        <div className="mb-5 max-w-sm">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 left-3 text-slateSoft pointer-events-none" />
            <input className={`${styles.input} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search office, officeholder, district" data-testid="gov-search" />
          </div>
        </div>

        {!list && <Loader2 className="w-6 h-6 animate-spin text-gold-deep" />}
        {list && (
          <div className={`${styles.card} overflow-x-auto`} data-testid="gov-table">
            <table className="w-full text-sm">
              <thead className="bg-cream-panel border-b border-borderSubtle text-navy/60 uppercase tracking-widest text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Office</th>
                  <th className="px-3 py-2 text-left">District</th>
                  <th className="px-3 py-2 text-left">Officeholder</th>
                  <th className="px-3 py-2 text-left">Verification</th>
                  <th className="px-3 py-2 text-left">Discovery</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-borderSubtle last:border-0 hover:bg-cream" data-testid={`gov-row-${r.seed_id || r.id}`}>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-navy">{r.display_name}</p>
                      <p className="text-xs text-slateSoft">{[r.seed_id, r.office_level, (r.office_chamber || "").replace(/_/g, " ")].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-slateSoft">{r.district_code || "—"}</td>
                    <td className="px-3 py-3 text-xs text-navy">
                      {r.officeholder_name || "—"}
                      {r.officeholder_party && <span className="text-slateSoft"> ({r.officeholder_party}{PARTY_LABEL[r.officeholder_party] ? "" : ""})</span>}
                      {r.officeholder_status && r.officeholder_status !== "current" && <span className="ml-1 text-amber-700 font-semibold">[{r.officeholder_status}]</span>}
                    </td>
                    <td className="px-3 py-3"><VerificationBadge value={r.verification_status} /></td>
                    <td className="px-3 py-3 text-xs">
                      {r.discovery_enabled && r.published && r.visibility === "public"
                        ? <span className={`${styles.chip} bg-green-50 text-green-800 border border-green-200`}>Visible</span>
                        : <span className={`${styles.chip} bg-navy/5 text-navy/60 border border-navy/15`}>Not visible</span>}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap space-x-1">
                      <button className={styles.btnGhost} onClick={() => setEditing(r)} data-testid={`gov-edit-${r.seed_id || r.id}`}><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                      {r.verification_status !== "verified"
                        ? <button className={styles.btnGhost} onClick={() => setVerifying(r)} data-testid={`gov-verify-${r.seed_id || r.id}`}><ShieldCheck className="w-3.5 h-3.5" /> Verify</button>
                        : <button className={styles.btnGhost} onClick={() => markStale(r)} data-testid={`gov-stale-${r.seed_id || r.id}`}><RotateCcw className="w-3.5 h-3.5" /> Mark stale</button>}
                      {r.website && <a href={r.website} target="_blank" rel="noreferrer" className={styles.btnGhost} data-testid={`gov-site-${r.seed_id || r.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slateSoft">No government resources match.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {editing && <GovEditDialog record={editing} onClose={() => setEditing(null)} onSave={save} />}
        {verifying && <VerifyDialog record={verifying} onClose={() => setVerifying(null)} onVerify={verify} />}
      </AdminShell>
    </AdminGate>
  );
}

function GovEditDialog({ record, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    display_name: record.display_name || "",
    legal_name: record.legal_name || "",
    description: record.description || "",
    website: record.website || "",
    phone: record.phone || "",
    email: record.email || "",
    address: record.address || "",
    office_level: record.office_level || "",
    office_chamber: record.office_chamber || "",
    district_code: record.district_code || "",
    officeholder_name: record.officeholder_name || "",
    officeholder_party: record.officeholder_party || "",
    officeholder_status: record.officeholder_status || "current",
    constituent_services_url: record.constituent_services_url || "",
    jurisdictions: (record.jurisdictions || []).join(", "),
    service_areas: (record.service_areas || []).join(", "),
    discovery_enabled: record.discovery_enabled ?? true,
    published: record.published ?? true,
    internal_notes: record.internal_notes || "",
    change_reason: "",
  }));
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const payload = { ...form };
    payload.jurisdictions = form.jurisdictions.split(",").map((s) => s.trim()).filter(Boolean);
    payload.service_areas = form.service_areas.split(",").map((s) => s.trim()).filter(Boolean);
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    if (!payload.change_reason) delete payload.change_reason;
    try {
      await onSave(payload);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-start justify-center p-6 overflow-y-auto" data-testid="gov-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-6 lg:p-8 space-y-4 my-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">Government resource</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">{record.display_name}</h2>
            {record.seed_id && <p className="mt-0.5 text-xs text-slateSoft">Seed record {record.seed_id}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="gov-dialog-error">{err}</p>}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Office / display name"><input className={styles.input} required value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="gov-display-name" /></Field>
          <Field label="Legal name"><input className={styles.input} value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} /></Field>
          <Field label="Office level">
            <select className={styles.input} value={form.office_level} onChange={(e) => set("office_level", e.target.value)} data-testid="gov-office-level">
              <option value="">—</option>
              {["federal", "state", "county", "municipal"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Chamber / body">
            <select className={styles.input} value={form.office_chamber} onChange={(e) => set("office_chamber", e.target.value)} data-testid="gov-office-chamber">
              <option value="">—</option>
              {["us_senate", "us_house", "pa_senate", "pa_house", "executive", "county", "municipal"].map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="District code"><input className={styles.input} value={form.district_code} onChange={(e) => set("district_code", e.target.value)} placeholder="statewide, PA-12, SD-37, HD-44" data-testid="gov-district" /></Field>
          <Field label="Officeholder status">
            <select className={styles.input} value={form.officeholder_status} onChange={(e) => set("officeholder_status", e.target.value)} data-testid="gov-holder-status">
              {["current", "vacant", "outgoing"].map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Current officeholder"><input className={styles.input} value={form.officeholder_name} onChange={(e) => set("officeholder_name", e.target.value)} data-testid="gov-holder-name" /></Field>
          <Field label="Party (factual metadata)">
            <select className={styles.input} value={form.officeholder_party} onChange={(e) => set("officeholder_party", e.target.value)} data-testid="gov-party">
              <option value="">—</option>
              {["D", "R", "I"].map((v) => <option key={v} value={v}>{PARTY_LABEL[v]}</option>)}
            </select>
          </Field>
          <Field label="Official website"><input className={styles.input} value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="gov-website" /></Field>
          <Field label="Constituent services link"><input className={styles.input} value={form.constituent_services_url} onChange={(e) => set("constituent_services_url", e.target.value)} data-testid="gov-constituent-url" /></Field>
          <Field label="Public phone"><input className={styles.input} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Public email"><input className={styles.input} value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
        </div>

        <Field label="Address"><input className={styles.input} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
        <Field label="Jurisdictions (comma separated)"><input className={styles.input} value={form.jurisdictions} onChange={(e) => set("jurisdictions", e.target.value)} placeholder="Pennsylvania, PA-12" /></Field>
        <Field label="Service areas (comma separated)"><input className={styles.input} value={form.service_areas} onChange={(e) => set("service_areas", e.target.value)} /></Field>
        <Field label="Description"><textarea className={`${styles.input} min-h-[70px]`} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Discovery visibility">
            <select className={styles.input} value={form.discovery_enabled && form.published ? "yes" : "no"} onChange={(e) => { set("discovery_enabled", e.target.value === "yes"); set("published", e.target.value === "yes"); }} data-testid="gov-discovery">
              <option value="yes">Visible in public Discovery</option>
              <option value="no">Not visible</option>
            </select>
          </Field>
        </div>

        {record.organization_role === "legislative_office" && (
          <p className="text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="gov-discovery-only-note">
            <ShieldAlert className="w-4 h-4" /> Legislative office — Discovery Only. Referral eligibility cannot be enabled during the pilot.
          </p>
        )}

        <Field label="Internal notes (admins only)"><textarea className={`${styles.input} min-h-[50px]`} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} /></Field>
        <Field label="Reason for change (audit log)"><input className={styles.input} value={form.change_reason} onChange={(e) => set("change_reason", e.target.value)} placeholder="e.g. Officeholder website corrected against official source" data-testid="gov-change-reason" /></Field>

        <div className="flex justify-end gap-3 pt-2 border-t border-borderSubtle">
          <button type="button" onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button type="submit" className={styles.btnGold} data-testid="gov-save">Save changes</button>
        </div>
      </form>
    </div>
  );
}

function VerifyDialog({ record, onClose, onVerify }) {
  const [source, setSource] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await onVerify(source, note);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Verification failed.");
    }
  };
  return (
    <div className="fixed inset-0 bg-navy-deep/70 backdrop-blur-sm z-40 flex items-center justify-center p-6" data-testid="gov-verify-dialog">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 lg:p-8 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow text-gold-deep">Verify record</p>
            <h2 className="mt-1 font-display font-black text-navy text-xl">{record.display_name}</h2>
            <p className="mt-1 text-xs text-slateSoft">Confirm office, officeholder, and contact details against an authoritative source (official .gov / .house.gov / .senate.gov page).</p>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
        <Field label="Verification source (required)"><input className={styles.input} required minLength={3} value={source} onChange={(e) => setSource(e.target.value)} placeholder="https://www.senate.gov/…" data-testid="gov-verify-source" /></Field>
        <Field label="Note (optional)"><textarea className={`${styles.input} min-h-[50px]`} value={note} onChange={(e) => setNote(e.target.value)} data-testid="gov-verify-note" /></Field>
        <div className="flex justify-end gap-3 pt-2 border-t border-borderSubtle">
          <button type="button" onClick={onClose} className={styles.btnGhost}>Cancel</button>
          <button type="submit" className={styles.btnGold} data-testid="gov-verify-submit"><ShieldCheck className="w-4 h-4" /> Mark verified</button>
        </div>
      </form>
    </div>
  );
}
